import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_KEY = 'lgra.cart.session_id';
const LAST_CART_KEY = 'lgra.cart.snapshot';

// Best-effort UUID v4. `crypto.randomUUID` is unavailable on older Safari.
const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const getSessionId = () => {
  try {
    let sid = window.localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = uuid();
      window.localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (_) {
    // localStorage disabled — fall back to an in-memory session so guest
    // cart still works within a single tab.
    return uuid();
  }
};

const buildHeaders = () => {
  const headers = {};
  const sid = getSessionId();
  if (sid) headers['X-Cart-Session'] = sid;
  return headers;
};

/**
 * CartProvider — persistent shopping cart context.
 *
 * Responsibilities:
 *   • Load the cart on mount (guest via X-Cart-Session, member via cookie).
 *   • Auto-merge a guest cart into the member cart on sign-in.
 *   • Expose add/remove/patch/clear operations for the UI.
 *   • Keep a lightweight local snapshot so the header badge count stays
 *     accurate across route changes without a network flash.
 */
export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState(() => {
    try {
      const raw = window.localStorage.getItem(LAST_CART_KEY);
      return raw ? JSON.parse(raw) : { items: [], item_count: 0 };
    } catch (_) { return { items: [], item_count: 0 }; }
  });
  const [loading, setLoading] = useState(false);
  const prevUserId = useRef(user?.id || null);
  // Global cart-drawer state so any add-to-cart trigger (booking modal,
  // package card, guest flow, etc.) can pop the drawer with the freshly
  // added item + a "Continue to Checkout" CTA — the confirmation UX
  // requested Feb 2026 (never redirect straight to payment, never lose
  // the customer's original selection).
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const persistLocal = (next) => {
    try { window.localStorage.setItem(LAST_CART_KEY, JSON.stringify(next)); } catch (_) { /* silent */ }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/cart`, {
        headers: buildHeaders(),
        withCredentials: true,
      });
      setCart(data);
      persistLocal(data);
      return data;
    } catch (e) {
      // Network hiccups shouldn't break navigation — fall back to snapshot.
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Explicit guest→member merge — callable from CartCheckout's inline
  // sign-in flow so we can sequence "sign-in → merge → reload quote"
  // and avoid racing the useEffect below. Returns the merged cart.
  const mergeGuestCart = useCallback(async () => {
    let sid;
    try { sid = window.localStorage.getItem(SESSION_KEY); } catch (_) { sid = null; }
    if (!sid) {
      await refresh();
      return null;
    }
    try {
      const { data } = await axios.post(
        `${API}/cart/merge`,
        { session_id: sid },
        { headers: buildHeaders(), withCredentials: true },
      );
      setCart(data);
      persistLocal(data);
      try { window.localStorage.removeItem(SESSION_KEY); } catch (_) { /* silent */ }
      return data;
    } catch (_) {
      await refresh();
      return null;
    }
  }, [refresh]);

  // Sign-in merge — when the user id transitions from null → set, POST
  // /cart/merge with the guest session id so the member cart absorbs the
  // guest cart without duplicates. Only fires when a guest session id
  // actually existed — otherwise a fresh signed-in page load would show
  // a misleading 'merged' toast even though nothing was merged.
  useEffect(() => {
    const prev = prevUserId.current;
    prevUserId.current = user?.id || null;
    if (!user?.id || prev === user.id) {
      // Still refresh so the header badge is right for both cases.
      refresh();
      return;
    }
    let sid;
    try { sid = window.localStorage.getItem(SESSION_KEY); } catch (_) { sid = null; }
    if (!sid) {
      // No guest cart to merge — just reload the member cart.
      refresh();
      return;
    }
    (async () => {
      // Capture the member cart size BEFORE the merge so we can prove
      // whether any items were actually absorbed. If the merge doesn't
      // grow the cart, we skip the toast to avoid the spurious 'guest
      // cart merged' banner on every fresh signed-in page load.
      let preMergeCount = 0;
      try {
        const { data: pre } = await axios.get(`${API}/cart`, {
          headers: buildHeaders(),
          withCredentials: true,
        });
        preMergeCount = pre?.item_count ?? (pre?.items || []).length;
      } catch (_) { /* silent */ }
      try {
        const { data } = await axios.post(
          `${API}/cart/merge`,
          { session_id: sid },
          { headers: buildHeaders(), withCredentials: true },
        );
        setCart(data);
        persistLocal(data);
        try { window.localStorage.removeItem(SESSION_KEY); } catch (_) { /* silent */ }
        const postMergeCount = data?.item_count ?? (data?.items || []).length;
        if (postMergeCount > preMergeCount) {
          toast.success('Your guest cart has been merged into your LGRA account.');
        }
      } catch (_) {
        // Silent fallback — just reload the member cart.
        await refresh();
      }
    })();
  }, [user?.id, refresh]);

  const addItem = useCallback(async (payload, options = {}) => {
    try {
      const { data } = await axios.post(`${API}/cart/items`, payload, {
        headers: buildHeaders(),
        withCredentials: true,
      });
      setCart(data);
      persistLocal(data);
      return data;
    } catch (e) {
      // Callers can opt out of the failure toast when the add is an
      // implicit / best-effort action (e.g. auto-adding from a legacy
      // ?package_id=... deep link).
      if (!options.silent) {
        toast.error(e?.response?.data?.detail || 'Could not add to cart');
      }
      throw e;
    }
  }, []);

  const removeItem = useCallback(async (item_key) => {
    try {
      const { data } = await axios.delete(`${API}/cart/items/${item_key}`, {
        headers: buildHeaders(),
        withCredentials: true,
      });
      setCart(data);
      persistLocal(data);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove item');
    }
  }, []);

  const patchItem = useCallback(async (item_key, patch) => {
    try {
      const { data } = await axios.patch(`${API}/cart/items/${item_key}`, patch, {
        headers: buildHeaders(),
        withCredentials: true,
      });
      setCart(data);
      persistLocal(data);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update item');
    }
  }, []);

  const clear = useCallback(async () => {
    try {
      const { data } = await axios.post(
        `${API}/cart/clear`, {}, {
          headers: buildHeaders(),
          withCredentials: true,
        },
      );
      setCart(data);
      persistLocal(data);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clear cart');
    }
  }, []);

  // Broadcast cart changes across tabs so a successful checkout in one tab
  // clears the other tab's icon/badge immediately (Aug 2026 Msg 205).
  useEffect(() => {
    const onStorage = (evt) => {
      if (evt.key === LAST_CART_KEY && evt.newValue) {
        try { setCart(JSON.parse(evt.newValue)); } catch (_) { /* silent */ }
      }
    };
    try { window.addEventListener('storage', onStorage); } catch (_) { /* silent */ }
    return () => {
      try { window.removeEventListener('storage', onStorage); } catch (_) { /* silent */ }
    };
  }, []);

  const value = useMemo(() => ({
    cart,
    itemCount: cart?.item_count || (cart?.items || []).length,
    loading,
    refresh,
    addItem,
    removeItem,
    patchItem,
    clear,
    mergeGuestCart,
    getSessionId,
    drawerOpen,
    openDrawer,
    closeDrawer,
  }), [cart, loading, refresh, addItem, removeItem, patchItem, clear, mergeGuestCart, drawerOpen, openDrawer, closeDrawer]);

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
